import { Capacitor, registerPlugin } from '@capacitor/core';

const BackgroundLocation = registerPlugin('BackgroundLocation');

let listenerHandle = null;
let nativeActive = false;
let nativeStarting = false;

const isNative = () => Capacitor.isNativePlatform?.() || Capacitor.getPlatform?.() === 'android';
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** True when the native foreground-location service is publishing — the JS
 *  geolocation watch then skips its own writes so we don't double-publish. */
export const isNativeLocationActive = () => nativeActive;

/**
 * While Android is starting the foreground service, the WebView must not
 * attach its own RTDB disconnect handler. Once native tracking owns presence,
 * a WebView socket blip must never mark that Anna offline.
 */
export const isNativeLocationPublishing = () => nativeActive || nativeStarting;

const confirmNativeServiceRunning = async () => {
  if (!BackgroundLocation?.isRunning) return false;
  // The Capacitor method resolves as soon as it asks Android to start the
  // service, not when Android has actually accepted the foreground service.
  // Do not hand off presence until the native side confirms it is alive.
  for (let attempt = 0; attempt < 5; attempt += 1) {
    try {
      const state = await BackgroundLocation.isRunning();
      if (state?.running === true) return true;
    } catch { /* keep the WebView fallback */ }
    await wait(250);
  }
  return false;
};

/**
 * Keep publishing Anna GPS while online/on-trip even if the app is backgrounded.
 * The native foreground service owns RTDB presence; JS receives positions for
 * its UI and becomes the fallback publisher only if native startup fails.
 */
export const startBackgroundLocation = async (onLocation) => {
  if (!isNative() || !BackgroundLocation?.start) return false;
  nativeStarting = true;
  try {
    if (listenerHandle?.remove) {
      await listenerHandle.remove();
      listenerHandle = null;
    }
    if (BackgroundLocation.addListener && onLocation) {
      listenerHandle = await BackgroundLocation.addListener('location', (data) => {
        if (data?.lat == null || data?.lng == null) return;
        onLocation({
          lat: Number(data.lat),
          lng: Number(data.lng),
          heading: data.heading != null && data.heading >= 0 ? Number(data.heading) : null,
          accuracy: data.accuracy != null && data.accuracy >= 0 ? Number(data.accuracy) : null,
        });
      });
    }
    await BackgroundLocation.start();
    if (!(await confirmNativeServiceRunning())) {
      throw new Error('native_location_service_not_running');
    }
    nativeActive = true;
    return true;
  } catch (e) {
    nativeActive = false;
    console.warn('background location start failed:', e?.message || e);
    return false;
  } finally {
    nativeStarting = false;
  }
};

export const stopBackgroundLocation = async () => {
  nativeActive = false;
  nativeStarting = false;
  if (!isNative() || !BackgroundLocation?.stop) return;
  try {
    if (listenerHandle?.remove) {
      await listenerHandle.remove();
      listenerHandle = null;
    }
    await BackgroundLocation.stop();
  } catch (e) {
    console.warn('background location stop failed:', e?.message || e);
  }
};

/**
 * Battery-optimization exemption state.
 *  - supported: false on web / pre-Android-6 → the UI hides the notice.
 *  - ignoring: true once the driver has granted it → the UI hides the notice.
 * Non-native callers get { supported: false, ignoring: true } so nothing shows.
 */
export const getBatteryOptimizationStatus = async () => {
  if (!isNative() || !BackgroundLocation?.getBatteryStatus) {
    return { supported: false, ignoring: true };
  }
  try {
    const res = await BackgroundLocation.getBatteryStatus();
    return {
      supported: res?.supported !== false,
      ignoring: res?.ignoring === true,
    };
  } catch (e) {
    console.warn('getBatteryOptimizationStatus failed:', e?.message || e);
    return { supported: false, ignoring: true };
  }
};

/**
 * Open the Android battery-optimization exemption dialog. USER-INITIATED ONLY —
 * wired to an explicit button in the onboarding card, never fired automatically.
 * Returns true if the system dialog was opened.
 */
export const requestBatteryOptimizationExemption = async () => {
  if (!isNative() || !BackgroundLocation?.requestBatteryExemption) return false;
  try {
    const res = await BackgroundLocation.requestBatteryExemption();
    return Boolean(res?.requested);
  } catch (e) {
    console.warn('requestBatteryOptimizationExemption failed:', e?.message || e);
    return false;
  }
};

/**
 * Tune native GPS cadence to what the moment needs:
 *   'active' — on a trip: high accuracy, ~2s (smooth trail + passenger dot)
 *   'idle'   — online & waiting: balanced power, ~6s (battery; 45s heartbeat covers presence)
 * No-op / stays on the current request if the plugin call fails — never worse than today.
 */
export const setLocationMode = async (mode) => {
  if (!isNative() || !BackgroundLocation?.setMode) return false;
  try {
    await BackgroundLocation.setMode({ mode: mode === 'active' ? 'active' : 'idle' });
    return true;
  } catch (e) {
    console.warn('setLocationMode failed (staying on current cadence):', e?.message || e);
    return false;
  }
};

/**
 * Native Android Background Trail Recorder (Runs even when WebView / screen is asleep)
 */
export const startNativeTripTrail = async (rideId, leg = 'pickup') => {
  if (!isNative() || !BackgroundLocation?.startTripTrail) return false;
  try {
    await BackgroundLocation.startTripTrail({ rideId: String(rideId || ''), leg: String(leg || 'pickup') });
    return true;
  } catch (e) {
    console.warn('startNativeTripTrail failed:', e?.message || e);
    return false;
  }
};

export const setNativeTripLeg = async (leg = 'trip') => {
  if (!isNative() || !BackgroundLocation?.setTripLeg) return false;
  try {
    await BackgroundLocation.setTripLeg({ leg: String(leg || 'trip') });
    return true;
  } catch (e) {
    console.warn('setNativeTripLeg failed:', e?.message || e);
    return false;
  }
};

export const getNativeTripTrail = async () => {
  if (!isNative() || !BackgroundLocation?.getTripTrail) return [];
  try {
    const res = await BackgroundLocation.getTripTrail();
    return Array.isArray(res?.points) ? res.points : [];
  } catch (e) {
    console.warn('getNativeTripTrail failed:', e?.message || e);
    return [];
  }
};

export const stopNativeTripTrail = async () => {
  if (!isNative() || !BackgroundLocation?.stopTripTrail) return false;
  try {
    await BackgroundLocation.stopTripTrail();
    return true;
  } catch (e) {
    console.warn('stopNativeTripTrail failed:', e?.message || e);
    return false;
  }
};
